import { Link } from 'react-router-dom';
import type { BlogPost } from '../../../mocks/blogPosts';

// Slugs that have dedicated full tutorial pages (not the generic post renderer)
const TUTORIAL_SLUGS = new Set([
  'google-ai-studio-free-api-key',
  'groq-free-api-key',
  'grok-xai-free-api-credits',
  'openrouter-free-api-key',
]);

function postPath(slug: string) {
  return `/blog/${slug}`;
}

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
}

export default function BlogCard({ post, featured = false }: BlogCardProps) {
  if (featured) {
    return (
      <Link to={postPath(post.slug)} className="group block cursor-pointer">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-gray-200 dark:border-[#2D3148] bg-white dark:bg-[#0D1117] hover:border-[#6366F1]/50 transition-all duration-300 hover:shadow-xl hover:shadow-[#6366F1]/5">
          <div className="relative overflow-hidden h-64 lg:h-auto">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10"></div>
            <span className="absolute top-4 left-4 px-3 py-1 bg-[#6366F1] text-white text-xs font-semibold rounded-full">
              Featured
            </span>
          </div>
          <div className="p-8 lg:p-10 flex flex-col justify-center">
            <span className="inline-block px-3 py-1 bg-[#6366F1]/10 text-[#818CF8] text-xs font-semibold rounded-full mb-4 w-fit">
              {post.category}
            </span>
            <h2 className="text-2xl lg:text-3xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight group-hover:text-[#6366F1] transition-colors">
              {post.title}
            </h2>
            <p className="text-gray-500 dark:text-[#94A3B8] text-base leading-relaxed mb-6 line-clamp-3">
              {post.excerpt}
            </p>
            <div className="flex items-center gap-4">
              <img
                src={post.author.avatar}
                alt={post.author.name}
                className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0"
              />
              <div>
                <div className="text-gray-900 dark:text-white text-sm font-semibold">{post.author.name}</div>
                <div className="text-gray-400 dark:text-[#64748B] text-xs">{post.date} · {post.readTime}</div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={postPath(post.slug)} className="group block cursor-pointer">
      <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-[#2D3148] bg-white dark:bg-[#0D1117] hover:border-[#6366F1]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#6366F1]/5 hover:-translate-y-1 h-full flex flex-col">
        <div className="relative overflow-hidden h-48 flex-shrink-0">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-6 flex flex-col flex-1">
          <span className="inline-block px-3 py-1 bg-[#6366F1]/10 text-[#818CF8] text-xs font-semibold rounded-full mb-3 w-fit">
            {post.category}
          </span>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 leading-snug group-hover:text-[#6366F1] transition-colors line-clamp-2 flex-1">
            {post.title}
          </h3>
          <p className="text-gray-500 dark:text-[#94A3B8] text-sm leading-relaxed mb-5 line-clamp-2">
            {post.excerpt}
          </p>
          <div className="flex items-center gap-3 mt-auto pt-4 border-t border-gray-100 dark:border-[#2D3148]">
            <img
              src={post.author.avatar}
              alt={post.author.name}
              className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0"
            />
            <div className="min-w-0">
              <div className="text-gray-900 dark:text-white text-xs font-semibold truncate">{post.author.name}</div>
              <div className="text-gray-400 dark:text-[#64748B] text-xs">{post.date} · {post.readTime}</div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
